import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Calendar, Settings, DollarSign, FileText, Users } from 'lucide-react';
import { useToast } from '../../hooks/useToast';
import { useOrganizador } from '../../contexts/OrganizadorContext';
import organizadoresService from '../../services/organizadoresService';
import catalogoPisteiroConfigService from '../../services/catalogoPisteiroConfigService';

// ─── Componente GTA/Nota Config ──────────────────────────────────────────────
function GtaNotaConfig({ organizadorId }) {
  const [config, setConfig] = useState({
    gta_valor_por_animal: 0,
    gta_valor_por_gta_nota: 0,
    gta_valor_taxa: 0,
    gta_modo_cobranca: 'por_vendedor',
  })
  const [saving, setSaving] = useState(false)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    if (!organizadorId) return
    organizadoresService.getConfiguracaoGtaNota(organizadorId)
      .then(data => { setConfig(data); setLoaded(true) })
      .catch(() => setLoaded(true))
  }, [organizadorId])

  const handleSave = async () => {
    setSaving(true)
    try {
      await organizadoresService.updateConfiguracaoGtaNota(organizadorId, config)
    } catch (err) {
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  if (!loaded) return null

  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <FileText size={20} className="text-amber-600" />
        Configurações GTA / Nota Fiscal
      </h3>
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Valor por Animal (R$)</label>
            <input
              type="number" step="0.01" min="0"
              value={config.gta_valor_por_animal}
              onChange={e => setConfig({ ...config, gta_valor_por_animal: parseFloat(e.target.value) || 0 })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Valor por GTA/Nota (R$)</label>
            <input
              type="number" step="0.01" min="0"
              value={config.gta_valor_por_gta_nota}
              onChange={e => setConfig({ ...config, gta_valor_por_gta_nota: parseFloat(e.target.value) || 0 })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Valor Taxa (R$)</label>
            <input
              type="number" step="0.01" min="0"
              value={config.gta_valor_taxa}
              onChange={e => setConfig({ ...config, gta_valor_taxa: parseFloat(e.target.value) || 0 })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Modo de Cobrança GTA/Nota</label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio" name="gta_modo"
                value="por_vendedor"
                checked={config.gta_modo_cobranca === 'por_vendedor'}
                onChange={e => setConfig({ ...config, gta_modo_cobranca: e.target.value })}
              />
              <span className="text-sm text-gray-700">Por Vendedor</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio" name="gta_modo"
                value="por_lote"
                checked={config.gta_modo_cobranca === 'por_lote'}
                onChange={e => setConfig({ ...config, gta_modo_cobranca: e.target.value })}
              />
              <span className="text-sm text-gray-700">Por Lote</span>
            </label>
          </div>
        </div>

        <div className="flex justify-end">
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50">
            {saving ? 'Salvando...' : 'Salvar GTA/Nota'}
          </button>
        </div>
      </div>
    </div>
  )
}

const Faturamento = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { organizadorAtivo, loadOrganizadorCompleto } = useOrganizador();

  const [configuracoes, setConfiguracoes] = useState({
    data_base_calculo: 'DATA_LEILAO', // DATA_LEILAO ou DATA_ATUAL
    tipo_calculo_parcelas: 'DIAS_CORRIDOS' // DIAS_CORRIDOS ou DIA_FIXO
  });

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Config de pisteiros (módulo premium)
  const [pisteiroConfig, setPisteiroConfig] = useState({ data_referencia: 'data_venda' });
  const temModuloPisteiros = organizadorAtivo?.produtos_premium?.includes('quem_vendeu_trouxe');
  // Esse módulo é o "Promotores (Leilão)" — % sobre o leilão inteiro. Não confundir
  // com o cadastro de Assessores (por lote), que não tem toggle aqui.
  const labelPromotores = 'Promotores';

  useEffect(() => {
    if (organizadorAtivo?.id) {
      carregarConfiguracoes();
      if (organizadorAtivo?.produtos_premium?.includes('quem_vendeu_trouxe')) {
        catalogoPisteiroConfigService.getConfig(organizadorAtivo.id)
          .then(cfg => setPisteiroConfig(prev => ({ ...prev, data_referencia: cfg.data_referencia || 'data_venda' })))
          .catch(() => {});
      }
    }
  }, [organizadorAtivo]);

  const carregarConfiguracoes = async () => {
    if (!organizadorAtivo?.id) return;
    try {
      setLoading(true);
      const config = await organizadoresService.getConfiguracaoFaturamento(organizadorAtivo.id);
      setConfiguracoes(config);
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };


  const handleSave = async () => {
    try {
      setSaving(true);
      await organizadoresService.updateConfiguracaoFaturamento(organizadorAtivo.id, configuracoes);
      if (loadOrganizadorCompleto) await loadOrganizadorCompleto(organizadorAtivo.id);

      // Salva data_referencia de pisteiros junto se tiver módulo
      if (temModuloPisteiros) {
        await catalogoPisteiroConfigService.saveDataReferencia(
          organizadorAtivo.id,
          pisteiroConfig.data_referencia
        );
      }

      toast.success('Configurações salvas com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      toast.error('Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setConfiguracoes(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/configuracoes')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Configurações de Faturamento</h1>
            <p className="text-gray-600 mt-1">
              Configure as regras padrão para cálculo de datas e parcelamentos
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="space-y-8">
            
            {/* Data Base para Cálculos */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Calendar size={20} className="text-blue-600" />
                Data Base para Cálculos
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors hover:bg-gray-50">
                    <input
                      type="radio"
                      name="data_base"
                      value="DATA_LEILAO"
                      checked={configuracoes.data_base_calculo === 'DATA_LEILAO'}
                      onChange={(e) => handleInputChange('data_base_calculo', e.target.value)}
                      className="mr-3"
                    />
                    <div>
                      <div className="font-medium text-gray-900">Data do Leilão</div>
                      <div className="text-sm text-gray-600">
                        Usar a data do leilão como ponto de partida para calcular prazos
                      </div>
                    </div>
                  </label>
                  
                  <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors hover:bg-gray-50">
                    <input
                      type="radio"
                      name="data_base"
                      value="DATA_ATUAL"
                      checked={configuracoes.data_base_calculo === 'DATA_ATUAL'}
                      onChange={(e) => handleInputChange('data_base_calculo', e.target.value)}
                      className="mr-3"
                    />
                    <div>
                      <div className="font-medium text-gray-900">Data Atual</div>
                      <div className="text-sm text-gray-600">
                        Usar a data de hoje como ponto de partida para calcular prazos
                      </div>
                    </div>
                  </label>
                </div>
                
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="text-sm text-blue-800">
                    <strong>Exemplo:</strong> Para um prazo de 30 dias:
                    <br />
                    • <strong>Data do Leilão:</strong> Se o leilão foi em 15/01, vencimento será 14/02
                    <br />
                    • <strong>Data Atual:</strong> Se hoje é 20/01, vencimento será 19/02
                  </div>
                </div>
              </div>
            </div>

            {/* Configurações de Parcelamento */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <DollarSign size={20} className="text-purple-600" />
                Configurações de Parcelamento
              </h3>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-4">
                    Tipo de Cálculo de Parcelas
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors hover:bg-gray-50">
                      <input
                        type="radio"
                        name="tipo_calculo_parcelas"
                        value="DIAS_CORRIDOS"
                        checked={configuracoes.tipo_calculo_parcelas === 'DIAS_CORRIDOS'}
                        onChange={(e) => handleInputChange('tipo_calculo_parcelas', e.target.value)}
                        className="mr-3"
                      />
                      <div>
                        <div className="font-medium text-gray-900">Dias Corridos</div>
                        <div className="text-sm text-gray-600">
                          Parcelas com intervalo fixo de dias (ex: 30, 60, 90 dias)
                        </div>
                      </div>
                    </label>
                    
                    <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors hover:bg-gray-50">
                      <input
                        type="radio"
                        name="tipo_calculo_parcelas"
                        value="DIA_FIXO"
                        checked={configuracoes.tipo_calculo_parcelas === 'DIA_FIXO'}
                        onChange={(e) => handleInputChange('tipo_calculo_parcelas', e.target.value)}
                        className="mr-3"
                      />
                      <div>
                        <div className="font-medium text-gray-900">Dia Fixo</div>
                        <div className="text-sm text-gray-600">
                          Parcelas sempre no mesmo dia do mês (ex: todo dia 15)
                        </div>
                      </div>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-4">
                    Comissão maior que a 1ª parcela (fluxo "1ª parcela")
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors hover:bg-gray-50">
                      <input
                        type="radio"
                        name="comissao_excedente_modo"
                        value="NEGATIVA"
                        checked={(configuracoes.comissao_excedente_modo || 'NEGATIVA') === 'NEGATIVA'}
                        onChange={(e) => handleInputChange('comissao_excedente_modo', e.target.value)}
                        className="mr-3"
                      />
                      <div>
                        <div className="font-medium text-gray-900">Descontar só da 1ª parcela</div>
                        <div className="text-sm text-gray-600">
                          Comportamento padrão. Se a comissão for maior que a 1ª parcela, ela fica negativa.
                        </div>
                      </div>
                    </label>

                    <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors hover:bg-gray-50">
                      <input
                        type="radio"
                        name="comissao_excedente_modo"
                        value="PROXIMA_PARCELA"
                        checked={configuracoes.comissao_excedente_modo === 'PROXIMA_PARCELA'}
                        onChange={(e) => handleInputChange('comissao_excedente_modo', e.target.value)}
                        className="mr-3"
                      />
                      <div>
                        <div className="font-medium text-gray-900">Descontar das próximas parcelas</div>
                        <div className="text-sm text-gray-600">
                          Se a comissão passar do valor da 1ª parcela, o excedente é descontado das parcelas seguintes, em ordem, até zerar.
                        </div>
                      </div>
                    </label>
                  </div>
                </div>

              </div>
            </div>

            {/* Configurações GTA/Nota removidas - usar Cadastros > GTA e Nota */}

          </div>

          {/* Módulo Promotores (vira "Assessores" no fluxo cavalo) */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <DollarSign size={20} className="text-indigo-600" />
              Módulo {labelPromotores}
            </h3>
            <div className="flex items-center justify-between p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
              <div>
                <p className="text-sm font-medium text-gray-800">Habilitar {labelPromotores}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Quando ativado, permite cadastrar {labelPromotores.toLowerCase()} e vinculá-los aos leilões para distribuição de comissão.
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleInputChange('promotores_habilitado', !configuracoes.promotores_habilitado)}
                className={`relative flex-shrink-0 w-12 h-7 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 ${configuracoes.promotores_habilitado ? 'bg-indigo-600' : 'bg-gray-300'}`}
              >
                <span className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${configuracoes.promotores_habilitado ? 'translate-x-5' : 'translate-x-0'}`} />
              </button>
            </div>
          </div>

          {/* Pisteiros — Data de Referência (apenas módulo premium) */}
          {temModuloPisteiros && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
                <Users size={20} className="text-purple-600" />
                Pisteiros — Data de Referência
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Define qual data é usada para filtrar o relatório de receita dos pisteiros.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {[
                  {
                    value: 'data_venda',
                    label: 'Data da venda',
                    desc: 'Filtra pela data do leilão (padrão)',
                  },
                  {
                    value: 'primeira_parcela',
                    label: 'Data da 1ª parcela',
                    desc: 'Filtra pelo vencimento da primeira parcela (igual sistema antigo)',
                  },
                ].map(({ value, label, desc }) => (
                  <label
                    key={value}
                    className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                      pisteiroConfig.data_referencia === value
                        ? 'border-purple-400 bg-purple-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="pisteiro_data_referencia"
                      value={value}
                      checked={pisteiroConfig.data_referencia === value}
                      onChange={() => setPisteiroConfig(prev => ({ ...prev, data_referencia: value }))}
                      className="mt-0.5"
                    />
                    <div>
                      <p className="font-medium text-gray-900">{label}</p>
                      <p className="text-sm text-gray-600">{desc}</p>
                    </div>
                  </label>
                ))}
              </div>

            </div>
          )}

          {/* Botões de Ação */}
          <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/configuracoes')}
              className="px-6 py-3 text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors"
              disabled={saving}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50"
              disabled={saving}
            >
              <Save size={20} />
              {saving ? 'Salvando...' : 'Salvar Configurações'}
            </button>
          </div>
        </div>
      </div>

      {/* Toast Messages */}
      <div className="fixed top-6 left-1/2 transform -translate-x-1/2 z-[9999] space-y-2">
        {toast.toasts.map((t) => (
          <div
            key={t.id}
            className={`px-8 py-4 rounded-xl shadow-2xl text-white text-lg font-semibold text-center min-w-80 ${
              t.type === 'success' ? 'bg-green-500' :
              t.type === 'error' ? 'bg-red-500' :
              t.type === 'warning' ? 'bg-yellow-500' :
              'bg-blue-500'
            }`}
          >
            {t.message}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Faturamento;